import { jsx } from '@emotion/react';
import cn from 'classnames';
import * as React from 'react';
import { useEffect, useMemo } from 'react';

import Html2React from '@/features/html2react';
import { Node } from '@/features/html2react/types';
import { processors } from '@/features/page/processors/functions/processors';
import { getStyledComponent } from '@/features/page/processors/functions/utils';

import { usePageContext } from '../../context';
import IntrinsicElements = jsx.JSX.IntrinsicElements;

type Props = {
  name: string;
  styleVariation: number;
  tagName?: keyof IntrinsicElements;
  id?: string;
  className?: string;
  children?: Node[];
};

const Pattern: React.FC<Props> = ({ name, styleVariation, tagName = 'div', id: anchor, className, children }) => {
  const { patterns } = usePageContext();
  const pattern = useMemo(() => (patterns ? patterns.find(p => p.name === name) : undefined), [name, patterns]);

  const id = useMemo(() => {
    if (anchor) return anchor;
    return pattern ? `pattern-${pattern.id}` : undefined;
  }, [anchor, pattern]);

  const StyledPattern = useMemo(() => {
    const Component = getStyledComponent(tagName);
    return Component`
      &#${id} {
        ${styleVariation > 0 ? (pattern?.styles?.[0] ?? '') : ''}
        ${pattern?.styles?.[styleVariation] ?? ''}
      }
    `;
  }, [id, pattern?.styles, styleVariation, tagName]);

  const memoizedProcessors = useMemo(() => processors('pattern'), []);

  useEffect(() => {
    if (!pattern?.scripts) return;

    const scriptId = `pattern-script-${pattern.id}`;

    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'text/javascript';
    script.innerHTML = pattern.scripts;
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [pattern?.id, pattern?.scripts]);

  return (
    <>
      {pattern?.isReusable ? (
        <StyledPattern id={`${id}`} className={cn('wp-block-erom-pattern', className)}>
          <Html2React
            isRoot={false}
            processors={memoizedProcessors}
            // innerProps={{ className: 'content-type-pattern' }}
          >
            {pattern?.originalContent}
          </Html2React>
        </StyledPattern>
      ) : (
        <StyledPattern id={`${id}`} className={cn('wp-block-erom-pattern', className)}>
          {children as React.ReactNode}
        </StyledPattern>
      )}
    </>
  );
};

export default Pattern;
